/**
 * «Связи» и «Согласия» в карточке человека (DC → Юнифанс → Аудитория), 06.10.2026.
 * Владелец: «мы должны уметь установить источник происхождения подписчика и однозначно понимать,
 * от кого получили разрешение на личные сообщения». Источник — QR игры у оператора, ссылка
 * тиммейта, личный QR оператора или сотрудник; у тех, кто пришёл до 06.10, — вывод из прежних
 * данных (помечен «до 06.10»). Согласие — одно (07.10): сообщения бота, галочка при «Старт»;
 * последнее решение: когда и как (галочка или /resume в боте, «стоп»).
 */
import { useEffect, useState } from "react";
import { api } from "../../api/client";

type Person = { tg: number; name: string; username: string };
type Consent = { kind: string; granted: boolean | null; ever?: boolean; at: string | null; method: string | null };
interface Origin {
  source: string;
  legacy: boolean;
  since: string | null;
  inviter: { tg: number; name: string | null } | null;
  staff: { id: string; name: string } | null;
  game: { id: number; date?: string; time?: string; quest?: string } | null;
  consents: Consent[];
  bot: Record<string, { n: number; last: string }>;
}

const SOURCE: Record<string, string> = {
  game_qr: "QR игры у оператора",
  teammate: "Ссылка участника игры",
  operator_recruit: "Личный QR оператора",
  staff_manual: "Добавил сотрудник",
  legacy_qr: "QR или ссылка",
  legacy_phone: "По номеру телефона",
  legacy_recruit: "Личный QR оператора",
};
const KIND: Record<string, string> = { messages: "Сообщения бота" };
const METHOD: Record<string, string> = { tma: "в приложении", bot: "в боте", staff: "сотрудник" };
/* во всю ширину карточки: строки длинные («QR игры у оператора», «да · 06.10 в приложении») */
const WIDE = { gridColumn: "1 / -1" } as const;

const day = (s: string | null | undefined) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}` : "");
/** Метки журнала — UTC (datetime('now')); показываем по времени филиалов, UTC+5. */
const dayLocal = (s: string | null | undefined) => {
  const t = s ? Date.parse(`${s.replace(" ", "T")}Z`) : NaN;
  return Number.isFinite(t) ? day(new Date(t + 5 * 3_600_000).toISOString()) : day(s);
};
const who = (p: { name: string | null; tg: number } | null | undefined, fb?: Person | null) =>
  p?.name || (fb ? fb.name || (fb.username ? `@${fb.username}` : "") : "") || (p ? `tg ${p.tg}` : "—");

function consentText(c: Consent): string {
  /* до 07.10 и до первого «Старт» галочки не было — это не отказ */
  if (c.granted === null) return "галочку ещё не отмечал";
  const what = c.granted ? "да" : c.method === "bot" ? "отписался" : "выключил";
  return `${what} · ${dayLocal(c.at)}${c.method ? ` ${METHOD[c.method] ?? c.method}` : ""}`;
}

export function DcPersonOrigin({ tg, invitedBy, invitedPeople }: { tg: number; invitedBy: Person | null; invitedPeople: Person[] }) {
  const [o, setO] = useState<Origin | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setO(null); setFailed(false);
    api<Origin>(`/api/audience/${tg}/origin`)
      .then((r) => { if (live) setO(r); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [tg]);

  const game = o?.game;
  const stops = o?.bot.stop?.n ?? 0;
  return (
    <>
      <div style={WIDE}>
        <h4>Связи</h4>
        <p><span>Откуда</span><b>{o ? `${SOURCE[o.source] ?? o.source}${o.legacy ? (o.since && o.since < "2026-10-06" ? " · до 06.10" : " · без записи допуска") : ""}` : failed ? "—" : "…"}</b></p>
        {game ? <p><span>Первая игра</span><b>{game.quest ? `${game.quest} ${day(game.date)} ${game.time ?? ""}` : `бронь ${game.id}`}</b></p> : null}
        {o?.staff ? <p><span>QR выдал</span><b>{o.staff.name}</b></p> : null}
        <p><span>Кто привёл</span><b>{o?.inviter || invitedBy ? who(o?.inviter, invitedBy) : "—"}</b></p>
        <p><span>Привёл сам ({invitedPeople.length})</span><b>{invitedPeople.length ? invitedPeople.map((x) => x.name || `@${x.username}`).join(", ") : "—"}</b></p>
      </div>
      <div style={WIDE}>
        <h4>Согласия</h4>
        {o ? o.consents.map((c) => (
          <p key={c.kind}><span>{KIND[c.kind] ?? c.kind}</span><b style={c.granted === false ? { color: "var(--red)" } : undefined}>{consentText(c)}</b></p>
        )) : <p><span>{failed ? "Не удалось загрузить" : "Загрузка…"}</span></p>}
        {stops ? <p><span>Писал боту «стоп»</span><b>{stops} · {dayLocal(o?.bot.stop?.last)}</b></p> : null}
      </div>
    </>
  );
}

/** Отметка в строке Аудитории (07.10, владелец: «чтобы каждому отображалось — поставил ли он галочку»). */
export function ConsentMark({ consent }: { consent?: number | null }) {
  const on = Number(consent) === 1;
  const off = consent === 0;
  const title = on ? "Поставил галочку «Согласен получать сообщения от бота»"
    : off ? "Отписался от бота (/stop) — галочки сейчас нет" : "Галочку в боте ещё не ставил";
  return (
    <span title={title} aria-label={title}
      style={{ flex: "0 0 auto", fontSize: 15, lineHeight: 1, fontWeight: 700, color: on ? "var(--green)" : off ? "var(--red)" : "var(--text-3)" }}>
      {on ? "☑" : "☐"}
    </span>
  );
}
